import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ShareService } from '@core/services/share-data/share-data.service';
import { SessionStorageService } from '@core/services/session-storage/session-storage.service';
import { OverviewLinkHandlerService } from '@core/services/overview-link-handler/overview-link-handler.service';
import { Subscription } from 'rxjs';
import { MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { LeaveModalComponent } from '@shared/modals/leave-modal/leave-modal.component';
import { CONFIG, SELECTORS, STORAGE_NAMES } from 'src/app/app.config';
import { SetupPageService } from '@core/services/setup-page/setup-page.service';
import { Location } from '@angular/common';
import { BlindProperties, HeaderShopMenu } from '@root/app.interfaces';

import * as _ from 'lodash';
import { ModalService } from '@core/services/modal/modal.service';
import { GtmService } from '@core/services/gtm/gtm.service';
import { GTM_HEADER_EVENTS } from '@core/services/gtm/gtm.events';
import { VariantService } from '@core/services/variant/variant.service';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { SlickCarouselComponent } from 'ngx-slick-carousel';
import { DepositSuccessModalComponent } from '@shared/modals/deposit-success-modal/deposit-success-modal.component';

@Component({
	selector: 'app-header',
	templateUrl: './header.component.html',
	styleUrls: ['./header.component.scss', './header.responsive.scss'],
})
export class HeaderComponent implements OnInit, OnDestroy {
	constructor(
		private location: Location,
		private shareDataService: ShareService,
		private modalService: ModalService,
		private sessionStorageService: SessionStorageService,
		private changeDetection: ChangeDetectorRef,
		private overviewLinkHandlerService: OverviewLinkHandlerService,
		private countryCodeService: SetupPageService,
		private gtm: GtmService,
		private variant: VariantService,
		private router: Router,
	) {}
	@ViewChild('wrapper') wrapper: ElementRef;
	@ViewChild('navMenu') navMenu: ElementRef;
	@ViewChild('slickCarousel') slickCarousel: SlickCarouselComponent;

	getBlindName: Subscription;
	getSessionBlindsData: Subscription;
	getBlindNameStatus: Subscription;
	getBlindStatus: Subscription;
	getBlindEmpty: Subscription;
	getBlindType: Subscription;
	getActionBackPage: Subscription;
	getCheckExit: Subscription;
	getEmbeddedStatus: Subscription;
	navigationSubscription: Subscription;
	dialogOpenedSubscription: Subscription;
	blindsAvailabilitySubs: Subscription;

	isModalOpened = false;
	isEmbedded = false;
	isBlindTypeSelected: boolean;
	isShop = this.variant.isShop();
	overviewStatus: boolean;
	shouldShowShopMenu = this.isShop;
	hasBlindPartsError = false;

	shopMenu: HeaderShopMenu[] = [HeaderShopMenu.Home, HeaderShopMenu.Overview, HeaderShopMenu.Quote, HeaderShopMenu.Deposit];
	activeShopMenuItem: HeaderShopMenu;

	sliderConfig = {
		dots: false,
		arrows: false,
		infinite: false,
		slidesToShow: 1,
		slidesToScroll: 1,
	};

	private isSliding = false;
	private pendingSlide: HeaderShopMenu | null = null;

	protected readonly sliderDisplayBreakpoint = 700;
	protected readonly window = window;

	ngOnInit(): void {
		const blindDataStorage = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_data);
		this.overviewStatus = _.isEmpty(blindDataStorage);

		this.getSessionBlindsData = this.shareDataService.getSessionBlindsData.subscribe(this.sessionBlindsHandler.bind(this));
		this.getBlindName = this.shareDataService.getBlindName.subscribe(this.hasBlinds.bind(this));
		this.getBlindNameStatus = this.shareDataService.getStatus.subscribe(this.blindStatusHandler.bind(this));
		this.getBlindEmpty = this.shareDataService.getBlindEmpty.subscribe(this.emptyBlindHandler.bind(this));

		this.getBlindType = this.shareDataService.getBlindType.subscribe((res: BlindProperties) => (this.isBlindTypeSelected = !!res.type));
		this.getCheckExit = this.shareDataService.getCheckExit.subscribe(this.checkExit.bind(this));
		this.getActionBackPage = this.shareDataService.getActionBackPage.subscribe((res) => {
			this.activeLinkHandler();
			this.setMainPageSize(res);
		});

		this.getEmbeddedStatus = this.shareDataService.getEmbeddedStatus.subscribe((res: boolean) => (this.isEmbedded = res));
		this.blindsAvailabilitySubs = this.shareDataService.getBlindsValidityStatus.subscribe((blindsValidity) => {
			this.hasBlindPartsError = Object.values(blindsValidity).some((blindValidity) => blindValidity.block);
		});

		this.checkExit();
		this.shopNavigationHandler();
	}

	ngOnDestroy(): void {
		this.getBlindName.unsubscribe();
		this.getSessionBlindsData.unsubscribe();
		this.getBlindStatus.unsubscribe();
		this.getBlindEmpty.unsubscribe();
		this.getActionBackPage.unsubscribe();
		this.getBlindType.unsubscribe();
		this.getCheckExit.unsubscribe();
		this.getEmbeddedStatus.unsubscribe();
		this.navigationSubscription.unsubscribe();
		this.blindsAvailabilitySubs.unsubscribe();
		this.dialogOpenedSubscription?.unsubscribe();
	}

	hasBlinds(isBlindEmpty: string): void {
		this.overviewStatus = !isBlindEmpty.length;
		this.isBlindTypeSelected = !!isBlindEmpty;
		this.changeDetection.markForCheck();
	}

	blindStatusHandler(status: boolean): void {
		this.overviewStatus = !status;
		this.changeDetection.markForCheck();
	}

	sessionBlindsHandler(status: boolean): void {
		const currentDomainLink = this.location.path().split('?');
		const currentLink = currentDomainLink[0].split('/')[1] || 'home';

		if (status && currentLink === 'overview') {
			setTimeout(this.overviewLinkHandlerService.addActiveClass.bind(this));
		}

		this.overviewStatus = !status;
		this.isBlindTypeSelected = status;
		this.changeDetection.markForCheck();
	}

	emptyBlindHandler(status: boolean): void {
		if (!status) {
			this.activeLinkHandler();
		}

		this.overviewStatus = status;
		this.isBlindTypeSelected = !status;
		this.changeDetection.markForCheck();
	}

	activeLinkHandler(): void {
		const isNavigationExists = this.navMenu;
		const isNavigationSliderExists = this.isShop && this.slickCarousel;

		if (isNavigationExists) {
			const navigationList = this.navMenu.nativeElement.querySelectorAll('li');
			const lastElement = Array.from(navigationList).pop() as Element;

			lastElement?.classList.remove('active');
		}

		if (isNavigationExists || isNavigationSliderExists) {
			this.shareDataService.setTaskbarPanel(true);
		}
	}

	setMainPageSize(status: boolean): void {
		const mainPageWrapper = document.querySelector(SELECTORS.wrapper) as HTMLElement;

		if (status && window.innerWidth > CONFIG.breakpoints['l-desktop']) {
			const header = this.wrapper.nativeElement.parentElement;

			setTimeout(() => {
				const headerHeight = header.offsetHeight;
				mainPageWrapper.style.height = `calc(100vh - ${headerHeight}px)`;

				window.dispatchEvent(new Event('resize'));
			}, 400);
		}

		this.changeDetection.markForCheck();
	}

	linkHandle(): void {
		this.gtm.pushEvent(GTM_HEADER_EVENTS.DESIGN_LINK);
	}

	overviewLinkHandler(status: boolean): void {
		const selectOpeningMode = this.shareDataService.getSelectOpeningModeValue();
		const blindsConfigured = this.variant.isShop() ? this.shareDataService.getBlindsConfiguredValue() : true; // Variant check needed as interior blind aren't wired to blind-state service but also a good catch all

		if (!status && !selectOpeningMode && blindsConfigured) {
			this.overviewLinkHandlerService.link();
		}

		this.gtm.pushEvent(GTM_HEADER_EVENTS.OVERVIEW_LINK);
	}

	openModal(e: Event, type?: string): void {
		e.preventDefault();
		const isProgressSaved = this.getProgressSaved();
		let dialogRef: MatDialogRef<any>;

		if (isProgressSaved) {
			dialogRef = this.modalService.openResponsiveDialog<{ embedded: boolean }>({
				component: LeaveModalComponent,
				data: { embedded: this.isEmbedded },
			});
			this.isModalOpened = true;
		} else {
			this.countryCodeService.resetOnBeforeUnloadHandler();
			this.countryCodeService.parseLocalLinkHandler();
		}

		if (dialogRef) {
			dialogRef.afterClosed().subscribe(() => {
				this.isModalOpened = false;
			});
		}

		this.gtm.pushEvent(GTM_HEADER_EVENTS[type.toUpperCase()]);
	}

	checkExit(): void {
		window.onbeforeunload = () => {
			return this.getProgressSaved() && !this.isModalOpened;
		};
	}

	getProgressSaved() {
		const storage = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_data);
		const isProgressSaved = this.sessionStorageService.getBlindData(STORAGE_NAMES.zip_blind_progress_saved);

		return !_.isEmpty(storage) && !isProgressSaved;
	}

	shopNavigationHandler(): void {
		this.navigationSubscription = this.router.events
			.pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
			.subscribe((event) => {
				this.activeShopMenuItem = event.url.includes('deposit_modal')
					? HeaderShopMenu.Deposit
					: (event.url
							.split('?')[0]
							.split('/')
							.filter((segment) => segment)[0] as HeaderShopMenu) || HeaderShopMenu.Home;

				if (this.slickCarousel) {
					this.goToCurrentMenuSlide(this.slickCarousel);
				}
			});

		this.shopNavigationModalsHandler();
	}

	shopNavigationModalsHandler(): void {
		this.dialogOpenedSubscription = this.shareDataService.getDialogState.subscribe((state) => {
			if (state.id === DepositSuccessModalComponent.ID) {
				this.shouldShowShopMenu = !state.isOpen;
			}

			this.changeDetection.detectChanges();
		});
	}

	onSliderInit({ slick }): void {
		this.isSliding = false;
		this.pendingSlide = null;
		this.goToCurrentMenuSlide(slick);
	}

	goToCurrentMenuSlide(slick: SlickCarouselComponent): void {
		if (this.isSliding) {
			this.pendingSlide = this.activeShopMenuItem;
			return;
		}

		this.isSliding = true;
		slick?.slickGoTo(this.shopMenu.indexOf(this.activeShopMenuItem));
	}

	onSlideChange(): void {
		this.isSliding = false;

		if (this.pendingSlide !== null) {
			this.activeShopMenuItem = this.pendingSlide;
			this.pendingSlide = null;
			setTimeout(this.goToCurrentMenuSlide.bind(this, this.slickCarousel));
		}
	}

	trackByIndex(index: number): number {
		return index;
	}
}
